import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';

import { FormattedMessage } from 'react-intl/dist';
import { fetchEnrollmentAndEnrolleeDetails, fetchPermissionDetails } from '../../../../actions/memberPortal/enrollmentsActionCreators';
import { ConditionalRendering } from '../../../../components/common/conditional-rendering';
import { ALL } from '../../../../constants/common/miscellaneous';
import useGetEnrollmentYears from '../../../../hooks/useGetEnrollmentYears';
import { useAppDispatch } from '../../utils';
import { IEnrollment } from '../common/types';
import { StyledMainSection } from '../styles/EnrollmentSharedStyled';
import AdditionalInformation from './AdditionalInformation';
import EnrollmentHistoryActions from './EnrollmentActions';
import EnrollmentDetails from './EnrollmentDetails';
import Header from './Header';
import TransactionActions from './TransactionActions';

const StyledCard = styled.div`
    border: ${({ theme }) => `1px solid ${theme.gray7}`};
    box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.05);
    border-radius: 4px;
    margin-bottom: 20px;
    background-color: #fff;
`;

const StyledCardContent = styled.div`
    padding: 20px;
`;

const NoEnrollment = styled.div`
    text-align: center;
    margin-top: 20px;
`;

function EnrollmentHistory() {
    const dispatch: Dispatch<any> = useAppDispatch();
    const { id, eId } = useParams();
    const [enrollments, setEnrollments] = useState<IEnrollment[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [permissions, setPermissions] = useState<string[]>([]);
    const [selectedYear, setSelectedYear] = useState<string>('');
    const { enrollmentandenrolleedetails } = useSelector((state: { memberEnrollment: any }) => state.memberEnrollment);
    const years = useGetEnrollmentYears(enrollmentandenrolleedetails);

    useEffect(() => {
        (async () => {
            setIsLoading(true);
            const formData = new FormData();
            formData.append('encHouseholdId', id);
            formData.append('csrftoken', window.csrf_token);
            dispatch(fetchPermissionDetails(id));
            await dispatch(fetchEnrollmentAndEnrolleeDetails(formData));
            setIsLoading(false);
        })();
    }, [id]);

    useEffect(() => {
        setEnrollments(getSortedEnrollments as IEnrollment[]);
        setPermissions(enrollmentandenrolleedetails?.userPermissionList);
    }, [enrollmentandenrolleedetails]);

    const getSortedEnrollments = useMemo(() => {
        let enrollments: IEnrollment[] = [];
        const enrollmentDataDtoListMap = enrollmentandenrolleedetails?.enrollmentDataDtoListMap;
        if (enrollmentDataDtoListMap) {
            for (var key in enrollmentDataDtoListMap) {
                enrollments = enrollmentDataDtoListMap[key];
                enrollments.sort((a: IEnrollment, b: IEnrollment) => {
                    return new Date(b.creationTimestamp).getTime() - new Date(a.creationTimestamp).getTime();
                }) || [];
            }
        }

        return enrollments;
    }, [enrollmentandenrolleedetails]);

    useEffect(() => {
        const filteredEnrollments =
            selectedYear !== ALL
                ? getSortedEnrollments?.filter(el => el.enrollmentBenefitEffectiveStartDate.includes(selectedYear))
                : getSortedEnrollments;
        setEnrollments(filteredEnrollments as IEnrollment[]);
    }, [selectedYear, getSortedEnrollments]);

    return (
        <>
            <Header years={years} selectedYear={selectedYear} setSelectedYear={setSelectedYear} />
            <StyledMainSection>
                <ConditionalRendering isTrue={enrollments.length === 0 && !isLoading}>
                    <NoEnrollment>
                        <p>
                            <FormattedMessage id={'member.enrollment_list.noEnrollmentFound'} />
                        </p>
                    </NoEnrollment>
                </ConditionalRendering>

                <ConditionalRendering isTrue={enrollments.length > 0}>
                    {enrollments.map((enrollment, index) => (
                        <StyledCard key={index} data-testid={`enrollment-card-${index}`}>
                            <StyledCardContent>
                                <EnrollmentDetails enrollment={enrollment} dataTestId={`enrollment-details-${index}`} />
                                <TransactionActions enrollment={enrollment} />
                                <AdditionalInformation
                                    enrollment={enrollment}
                                    dataTestId={`additional-information-${index}`}
                                />
                            </StyledCardContent>
                            <EnrollmentHistoryActions enrollment={enrollment} permissions={permissions} />
                        </StyledCard>
                    ))}
                </ConditionalRendering>
            </StyledMainSection>
        </>
    );
}

export default EnrollmentHistory;
